import { initializeApp, getApps, getApp } from "firebase/app";
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signInAnonymously, 
  updateProfile, 
  linkWithPopup, 
  signOut as fbSignOut, 
  onAuthStateChanged, 
  type User as FirebaseUser 
} from "firebase/auth";
import { 
  getFirestore, 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  deleteField, 
  onSnapshot, 
  query, 
  where, 
  orderBy, 
  writeBatch, 
  arrayUnion, 
  arrayRemove, 
  serverTimestamp 
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import type { UserProfile, YouTubeSearchResult } from "../types";
import { generatePartyCode } from "./utils";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyAWQB5Tf9vko9z7h4mwHrpOir-DzmGr7nM",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "squad-a-roke.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "squad-a-roke",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "squad-a-roke.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "819077227448",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:819077227448:web:123456789abcdef"
};

const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, "us-central1");

// --- Auth Helpers ---
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

export async function loginWithGoogle() {
  return await signInWithPopup(auth, googleProvider);
}

export async function loginAnonymously(nickname: string) {
  const userCredential = await signInAnonymously(auth);
  if (userCredential.user) {
    await updateProfile(userCredential.user, {
      displayName: nickname.trim() || "Guest Singer",
      photoURL: `https://api.dicebear.com/7.x/bottts/svg?seed=${encodeURIComponent(nickname.trim() || userCredential.user.uid)}`
    });
  }
  return userCredential;
}

export async function linkAnonymousWithGoogle() {
  if (!auth.currentUser) throw new Error("No active user to link.");
  return await linkWithPopup(auth.currentUser, googleProvider);
}

export async function logoutUser() {
  return await fbSignOut(auth);
}

// --- Cloud Functions Callables ---
export const searchYoutubeFn = httpsCallable<{ query: string }, { items: any[] }>(functions, "searchYoutube");

/**
 * Searches YouTube tracks via Cloud Function with direct API fallback.
 */
export async function searchYoutube(queryText: string): Promise<any[]> {
  try {
    const result = await searchYoutubeFn({ query: `${queryText} karaoke` });
    if (result?.data?.items) {
      return result.data.items;
    }
  } catch (err: any) {
    console.warn("searchYoutube Cloud Function error, trying fallback:", err);
    const apiKey = import.meta.env.VITE_YOUTUBE_API_KEY;
    if (apiKey) {
      const params = new URLSearchParams({
        key: apiKey,
        part: "snippet",
        q: `${queryText} karaoke`,
        maxResults: "12",
        type: "video",
      });
      const res = await fetch(`https://www.googleapis.com/youtube/v3/search?${params}`);
      if (res.ok) {
        const data = await res.json();
        return data.items || [];
      }
    }
    throw err;
  }
  return [];
}

// --- High-Level Resilient Service API ---

/**
 * Creates a party in Firestore with sequential steps ensuring security rules pass.
 */
export async function createParty(partyName: string, user: UserProfile): Promise<{ partyId: string; partyCode: string; partyName: string; createdAt: any }> {
  const partyCode = generatePartyCode();
  const partyDocRef = doc(collection(db, "parties"));
  const partyId = partyDocRef.id;

  // 1. Create party document (auth.uid == hostId)
  await setDoc(partyDocRef, {
    partyName,
    partyCode,
    hostId: user.uid,
    hostName: user.displayName || "Host",
    status: "active",
    createdAt: serverTimestamp(),
    isPlaying: true,
  });

  // 2. Update user document (sets currentPartyId so isPartyMember evaluates to true)
  const userRef = doc(db, "users", user.uid);
  await setDoc(userRef, { currentPartyId: partyId }, { merge: true });

  // 3. Add host to members subcollection (isOwner matches memberId)
  const memberRef = doc(db, "parties", partyId, "members", user.uid);
  await setDoc(memberRef, {
    name: user.displayName || "Host",
    photoURL: user.photoURL || "",
    role: "HOST",
    uid: user.uid,
    isAnonymous: user.isAnonymous ?? false,
    joinedAt: Date.now(),
  });

  // 4. Initialize queueOrder/main
  const queueOrderRef = doc(db, "parties", partyId, "queueOrder", "main");
  await setDoc(queueOrderRef, {
    videoIds: [],
  });

  return {
    partyId,
    partyCode,
    partyName,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Joins a party by 6-char code using Firestore.
 */
export async function joinParty(partyCode: string, user: UserProfile): Promise<{ partyId: string; partyName: string; partyCode: string; hostId: string; hostName: string; createdAt: any }> {
  const cleanCode = partyCode.trim().toUpperCase();
  const partiesRef = collection(db, "parties");
  const q = query(partiesRef, where("partyCode", "==", cleanCode));
  const snap = await getDocs(q);

  if (snap.empty) {
    throw new Error("No party found with that 6-character code. Please double-check the code and try again.");
  }

  const partyDoc = snap.docs[0];
  const partyId = partyDoc.id;
  const partyData = partyDoc.data();

  // 1. Update user currentPartyId
  const userRef = doc(db, "users", user.uid);
  await setDoc(userRef, { currentPartyId: partyId }, { merge: true });

  // 2. Add member to party
  const memberRef = doc(db, "parties", partyId, "members", user.uid);
  await setDoc(memberRef, {
    name: user.displayName || "Guest Singer",
    photoURL: user.photoURL || "",
    role: "GUEST",
    uid: user.uid,
    isAnonymous: user.isAnonymous ?? false,
    joinedAt: Date.now(),
  });

  return {
    partyId,
    partyName: partyData.partyName || "Karaoke Party",
    partyCode: cleanCode,
    hostId: partyData.hostId || "",
    hostName: partyData.hostName || "Host",
    createdAt: partyData.createdAt || new Date().toISOString(),
  };
}

/**
 * Leaves a party cleanly.
 */
export async function leaveParty(partyId: string, uid: string): Promise<void> {
  const userRef = doc(db, "users", uid);
  await updateDoc(userRef, { currentPartyId: deleteField() });

  const memberRef = doc(db, "parties", partyId, "members", uid);
  await deleteDoc(memberRef);
}

/**
 * Ends a party (Host only) and purges all subcollections in a single batch.
 */
export async function endParty(partyId: string): Promise<void> {
  const batch = writeBatch(db);

  try {
    // 1. Fetch subcollection documents
    const [queueSnap, queueOrderSnap, membersSnap] = await Promise.all([
      getDocs(collection(db, "parties", partyId, "queue")),
      getDocs(collection(db, "parties", partyId, "queueOrder")),
      getDocs(collection(db, "parties", partyId, "members")),
    ]);

    // 2. Queue for deletion
    queueSnap.forEach((d) => batch.delete(d.ref));
    queueOrderSnap.forEach((d) => batch.delete(d.ref));
    membersSnap.forEach((d) => batch.delete(d.ref));

    // 3. Delete the parent party document
    batch.delete(doc(db, "parties", partyId));

    // 4. Commit all deletions atomically
    await batch.commit();
  } catch (err) {
    console.warn("Cascade delete batch error, deleting party doc directly:", err);
    await deleteDoc(doc(db, "parties", partyId));
  }
}

/**
 * Adds a song to the active party queue directly via Firestore.
 */
export async function addSongToQueue(partyId: string, song: YouTubeSearchResult, user: UserProfile): Promise<void> {
  const videoId = song.id.videoId;
  const queueSongData = {
    id: { kind: "youtube#video", videoId },
    videoId,
    snippet: {
      title: song.snippet.title,
      channelTitle: song.snippet.channelTitle,
      thumbnails: song.snippet.thumbnails,
    },
    title: song.snippet.title,
    channelTitle: song.snippet.channelTitle,
    thumbnailUrl: song.snippet.thumbnails.medium?.url || song.snippet.thumbnails.default?.url || "",
    status: {
      embeddable: song.status?.embeddable ?? true,
    },
    addedByUserId: user.uid,
    addedByUserName: user.displayName,
    addedByUserPhoto: user.photoURL || null,
    timestamp: Date.now(),
  };

  const songDocRef = doc(db, "parties", partyId, "queue", videoId);
  const orderDocRef = doc(db, "parties", partyId, "queueOrder", "main");

  await setDoc(songDocRef, queueSongData);
  await setDoc(orderDocRef, { videoIds: arrayUnion(videoId) }, { merge: true });
}

/**
 * Removes a song from the active queue directly via Firestore.
 */
export async function removeSongFromQueue(partyId: string, videoId: string): Promise<void> {
  const songDocRef = doc(db, "parties", partyId, "queue", videoId);
  const orderDocRef = doc(db, "parties", partyId, "queueOrder", "main");

  await deleteDoc(songDocRef);
  await setDoc(orderDocRef, { videoIds: arrayRemove(videoId) }, { merge: true });
}

/**
 * Reorders songs in the queue order document directly via Firestore.
 */
export async function reorderQueue(partyId: string, _videoId: string, oldIndex: number, newIndex: number, currentOrder: string[]): Promise<void> {
  const updatedOrder = [...currentOrder];
  const [moved] = updatedOrder.splice(oldIndex, 1);
  updatedOrder.splice(newIndex, 0, moved);
  const orderDocRef = doc(db, "parties", partyId, "queueOrder", "main");
  await updateDoc(orderDocRef, { videoIds: updatedOrder });
}

export {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  onSnapshot,
  query,
  where,
  orderBy,
  writeBatch,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  onAuthStateChanged,
  type FirebaseUser
};
